import { useEffect } from "react";
import { useScrollDepth } from "@/lib/scrollTracking";
import { Link } from "react-router-dom";
import Navbar from "@/components/landing/Navbar";
import Footer from "@/components/landing/Footer";
import { trackEvent } from "@/lib/observability";
import { trackFunnelEvent } from "@/lib/funnelAnalytics";
import { useCanonical } from "@/lib/useCanonical";

const persist = trackFunnelEvent as unknown as (
  name: string,
  meta: Record<string, unknown>,
) => void;

function trackSupport(event: string, meta: Record<string, unknown>) {
  const payload = { page: "support", ...meta };
  trackEvent(event, payload);
  persist(event, payload);
}

interface FaqItemProps {
  id: string;
  question: string;
  children: React.ReactNode;
}

/** Native <details>/<summary> accordion with expand/collapse tracking. */
function FaqItem({ id, question, children }: FaqItemProps) {
  return (
    <details
      className="group border-b border-border/40 py-4"
      onToggle={(e) => {
        const open = (e.currentTarget as HTMLDetailsElement).open;
        trackSupport("support_faq_toggle", {
          faq_id: id,
          question,
          state: open ? "open" : "closed",
        });
      }}
    >
      <summary
        className="font-display text-lg text-foreground tracking-tight cursor-pointer list-none flex items-center justify-between gap-4"
        onClick={() =>
          trackSupport("support_faq_click", { faq_id: id, question })
        }
      >
        <span>{question}</span>
        <span
          aria-hidden="true"
          className="text-muted-foreground transition-transform group-open:rotate-45"
        >
          +
        </span>
      </summary>
      <div className="pt-3 leading-relaxed">{children}</div>
    </details>
  );
}

const Support = () => {
  useScrollDepth("support");
  useCanonical({ path: "/support" });
  useEffect(() => {
    document.title = "Support | Myelina Health";
    const desc =
      "Help and answers for Myelina Health, a wellness energy companion for people living with MS. Contact our team and read common questions.";
    let meta = document.querySelector('meta[name="description"]') as HTMLMetaElement | null;
    if (!meta) {
      meta = document.createElement("meta");
      meta.name = "description";
      document.head.appendChild(meta);
    }
    meta.content = desc;
  }, []);

  const trackLink = (linkId: string, destination: string) =>
    trackSupport("support_link_click", { link_id: linkId, destination });

  return (
    <div className="min-h-screen bg-background flex flex-col">
      <Navbar />
      <main className="flex-1 pt-24 pb-16">
        <article className="container mx-auto px-6 sm:px-10 lg:px-16 max-w-3xl">
          <header className="mb-10">
            <h1 className="font-display text-4xl md:text-5xl tracking-tight text-foreground mb-3">
              Support
            </h1>
            <p className="text-base text-muted-foreground leading-relaxed">
              A quiet inbox on our side. Ask what you need, we listen closely, and we
              answer in your own time.
            </p>
          </header>

          <div className="text-muted-foreground space-y-6">
            <h2 className="font-display text-2xl text-foreground tracking-tight mt-10 mb-3">
              Contact
            </h2>
            <p className="leading-relaxed">
              Write to us at{" "}
              <a
                href="mailto:hello@myelinahealth.com"
                className="text-primary underline"
                onClick={() =>
                  trackLink("contact_email", "mailto:hello@myelinahealth.com")
                }
              >
                hello@myelinahealth.com
              </a>
              . A real person reads every note.
            </p>

            <h2 className="font-display text-2xl text-foreground tracking-tight mt-10 mb-3">
              Frequently asked questions
            </h2>

            <FaqItem id="data-handling" question="How is my data handled?">
              Your data stays yours. We never sell it. Export or delete anything you&rsquo;ve
              entered, in your own time. It&rsquo;s encrypted and access-controlled from the
              first check-in. Full detail lives in our{" "}
              <Link
                to="/privacy"
                className="text-primary underline"
                onClick={() => trackLink("faq_privacy_inline", "/privacy")}
              >
                Privacy Policy
              </Link>
              .
            </FaqItem>

            <FaqItem id="medical-advice" question="Is this medical advice?">
              No | Myelina is a wellness companion, not a medical device. Your care team
              stays the source of truth. Bring what you notice here into that conversation.
            </FaqItem>

            <FaqItem
              id="apple-health"
              question="How do I manage Apple Health access?"
            >
              iPhone Settings → Privacy &amp; Security → Health → Myelina Health, or in-app
              Settings → Apple Health. Change what you share, whenever it feels right.
            </FaqItem>

            <p className="pt-8 text-sm">
              For privacy questions, see our{" "}
              <Link
                to="/privacy"
                className="text-primary underline"
                onClick={() => trackLink("privacy_footer_link", "/privacy")}
              >
                Privacy Policy
              </Link>
              .
            </p>
          </div>
        </article>
      </main>
      <Footer />
    </div>
  );
};

export default Support;

